import { useState } from "react";
import { useTranslation } from "react-i18next";
import { preCreateProviderAllocations } from "../../api/identityQueue";
import { useSession } from "../../session/SessionContext";
import { useToast } from "../toast/toastContext";
import adminStyles from "./Admin.module.css";
import { Modal } from "./Modal";
import panel from "./AdminPanel.module.css";

const AdministerPermission = "entitlement.administer";

/**
 * Gives every active person who has no provider allocation a first one, from their usage in the last 30 days: the providers they used
 * are switched on, flat-fee providers are paid their seat fee, and the rest of their allowance is split equally across the metered
 * providers they used. People who already have an allocation are never changed. It asks first, in a dialog, and reports what it did as a
 * toast. Shown only to a person with `entitlement.administer`, which the server requires.
 */
export function PreCreateAllocationsPanel(): React.JSX.Element | null {
  const { t } = useTranslation();
  const toast = useToast();
  const { snapshot } = useSession();
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);

  if (snapshot.status !== "signed-in" || !snapshot.permissions.includes(AdministerPermission)) {
    return null;
  }

  const run = async () => {
    setRunning(true);
    const result = await preCreateProviderAllocations();
    setRunning(false);
    setConfirming(false);
    if (result.kind === "ok") {
      toast.show(
        t("admin.identity.preCreate.summary", {
          created: result.response.created,
          noUsage: result.response.noUsageOrSeat,
          noAllowance: result.response.noAllowance,
          seatFees: result.response.seatFeesExceedAllowance,
          refused: result.response.refused.length,
        }),
        "success",
      );
    } else {
      toast.show(result.kind === "denied" ? t("admin.denied.description", { permissions: AdministerPermission }) : result.message, "error");
    }
  };

  return (
    <section className={panel.panel} aria-labelledby="pre-create-allocations-heading">
      <div className={panel.header}>
        <h2 id="pre-create-allocations-heading" className={`panel-title ${panel.title}`}>
          {t("admin.identity.preCreate.title")}
        </h2>
        <button type="button" className="button button-secondary" onClick={() => setConfirming(true)} disabled={running}>
          {t("admin.identity.preCreate.run")}
        </button>
      </div>
      <p className={panel.intro}>{t("admin.identity.preCreate.description")}</p>

      {confirming && (
        <Modal title={t("admin.identity.preCreate.title")} onClose={() => setConfirming(false)}>
          <p>{t("admin.identity.preCreate.confirm")}</p>
          <div className={adminStyles.syncActions}>
            <button type="button" className="button button-secondary" onClick={() => setConfirming(false)} disabled={running}>
              {t("admin.identity.seatless.cancel")}
            </button>
            <button type="button" className="button button-primary" data-autofocus onClick={() => void run()} disabled={running}>
              {t("admin.identity.preCreate.confirmRun")}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
